import { useContext } from 'react';

import {
  Badge,
  Box,
  Button,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { AiOutlineFileSearch } from 'react-icons/ai';
import { MdKeyboardArrowDown } from 'react-icons/md';

import { Deal } from '../../../gql/dealGql';
import {
  useFillMessageTemplateMutation,
  useMessageTemplatesByTypeQuery,
} from '../../../gql/generated/graphql';

import { RecipientTypeMap } from '../../../constants/notifications';
import { logger } from '../../../libs/Logger';
import { TextWidgetContext } from '../../../libs/contextLib';
import { getConversationBoxes, isActiveBox } from '../../../utils/messages';

type TemplateSelectProps = {
  deal: Deal | null;
};

const TemplateSelect = ({ deal }: TemplateSelectProps) => {
  const { textDealId, activeConversation } = useContext(TextWidgetContext);
  const { setFieldValue } = useFormikContext();
  const { data: templateData } = useMessageTemplatesByTypeQuery();
  const [fillMessageTemplate] = useFillMessageTemplateMutation();
  const templates = templateData?.messageTemplatesByType ?? [];

  const handleTemplateClick = async (templateId: number) => {
    const recipientPhone = activeConversation.phone_number;
    if (textDealId == null || !recipientPhone) {
      return;
    }
    const activeBox = deal
      ? getConversationBoxes({ deal }).find((box) => isActiveBox({ box, activeConversation }))
      : undefined;

    try {
      const { data } = await fillMessageTemplate({
        variables: {
          input: {
            templateId,
            dealId: textDealId,
            recipientPhone,
            ...(activeBox?.personId != null && {
              recipientType: RecipientTypeMap[activeBox.role],
              recipientId: activeBox.personId,
            }),
          },
        },
      });

      if (data?.fillMessageTemplate.renderedMessage != null) {
        setFieldValue('text_message', data.fillMessageTemplate.renderedMessage);
      }
    } catch (error) {
      logger.error('TemplateSelect.tsx', 'Failed to fill message template', null, error);
    }
  };

  return (
    <Popover placement="left-start">
      <PopoverTrigger>
        <Button variant="secondary" size="sm">
          <AiOutlineFileSearch size={20} />
          <MdKeyboardArrowDown size={15} />
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <Box
          bgColor="white"
          boxShadow="md"
          pt={2}
          pb={2}
          overflowY="scroll"
          h="200px"
          onWheel={(e) => {
            // Stops the wheel event from bubbling up, avoiding scroll conflicts with parent elements.
            e.stopPropagation();
          }}
        >
          {templates.map((template) => {
            if (template?.id == null) {
              return null;
            }

            return (
              <Box
                p={2}
                _hover={{ bgColor: 'gray.200', cursor: 'pointer' }}
                key={template.id}
                onClick={() => handleTemplateClick(template.id as number)}
              >
                <HStack spacing={2}>
                  <Badge colorScheme="gray" fontSize="xs">
                    {template.category}
                  </Badge>
                  <Text fontSize="sm" noOfLines={1}>
                    {template.title}
                  </Text>
                </HStack>
              </Box>
            );
          })}
        </Box>
      </PopoverContent>
    </Popover>
  );
};

export default TemplateSelect;
